// sms.component.ts - Updated with proper translation state management

import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
  forwardRef,
  inject,
} from '@angular/core';
import {
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { templateServices } from 'src/app/services/template.service';
import { firstValueFrom, Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { advanceSearchService } from 'src/app/services/advance-search';
import { PickerComponent } from '@ctrl/ngx-emoji-mart';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SettingsDialogComponent } from '../default-template-fall/default-template-fall.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslationService } from 'src/app/services/translante.service';
import { TranslationDialogComponent } from '../html-editor/link/translante';

@Component({
  selector: 'app-sms',
  standalone: true,
  imports: [
    CommonModule, 
    ReactiveFormsModule, 
    PickerComponent,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
    MatInputModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatSnackBarModule
  ],
  templateUrl: './sms.component.html',
  styleUrls: ['./sms.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SmsComponent),
      multi: true,
    },
  ],
})
export class SmsComponent implements OnInit, AfterViewInit {
  @Input() tagsChanged: string = '';
  @Input() placeholder: string = 'Your input';
  @Input() title: string = 'Message';
  @Input() formControl!: FormControl;
  @Input() height: string = '150px';
  @Input() isSendingMessage: boolean = false;
  @Input() lead: any;
  @Input() allowConvert: boolean = true;
  
  private translationService = inject(TranslationService);
  private snackBar = inject(MatSnackBar);
  
  leadTags: any = [];
  templates: any = [];
  otherFields: any = [];
  recent: any = [];
  value: any = '';
  allFields:any;
  

  // Translation state
  private originalContent: string = '';
  isTranslated: boolean = false;  // Made public for template access
  isTranslating: boolean = false;

  // UI state for emoji picker (only still needed for this component)
  showEmojiPicker: boolean = false;

  // Pagination and search state
  templateSearchControl = new FormControl('');
  templateSearchSubject = new Subject<string>();
  templatePageIndex = 1;
  templatePageSize = 10;
  templateTotal = 0;
  isLoadingTemplates = false;
  isLoadingMoreTemplates = false;
  hasMoreTemplates = true;

  currentTemplate: string = '';
  @ViewChild('textarea') textarea!: ElementRef;
  @ViewChild('templateMenuTrigger', { read: MatMenuTrigger }) templateMenuTrigger!: MatMenuTrigger;
  @ViewChild('templateMenuContent') templateMenuContent!: ElementRef;
  @ViewChild('templateSearchInput') templateSearchInput!: ElementRef;
  @Output() templateChosen = new EventEmitter<any>();

  constructor(
    private templateService: templateServices, 
    private advanceSearch: advanceSearchService,
    private dialog: MatDialog
  ) {}

  // Only needed for emoji picker which is still using custom dropdown
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.emoji-picker-container') && 
        !target.closest('.emoji-btn')) {
      this.showEmojiPicker = false;
    }
  }

  @HostListener('paste', ['$event'])
onPaste(event: ClipboardEvent): void {
  const clipboardData = event.clipboardData;
  if (!clipboardData) return;

  // Check if clipboard contains files (images/videos)
  const items = clipboardData.items;
  let hasImage = false;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    // Check if the item is an image or video file
    if (item.type.indexOf('image') !== -1 || item.type.indexOf('video') !== -1) {
      hasImage = true;
      break;
    }
  }

  // If image or video found, prevent paste and show message
  if (hasImage) {
    event.preventDefault();
    this.snackBar.open('Images and videos are not allowed', 'Close', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
  }
}

  ngOnInit(): void {
    this.loadTags();
    
    // Only load templates if isSendingMessage is true
    if (this.isSendingMessage) {
      this.loadTemplates();
      this.setupTemplateSearch();
    }

    // Monitor form control changes to reset translation state
    if (this.formControl) {
      this.formControl.valueChanges.subscribe(value => {
        // If content changed and we're in translated state, reset
        if (this.isTranslated && value !== this.getTranslatedContent()) {
          this.resetTranslationState();
        }
      });
    }
  }

  ngAfterViewInit(): void {
    // Focus on the textarea once the view is initialized
    setTimeout(() => {
      this.textarea.nativeElement.focus();
    });
  }

  // Get the current content (either original or translated)
  private getTranslatedContent(): string {
    return this.formControl.value || '';
  }

  // Method to reset translation state - Made public for template access
  resetTranslationState(): void {
    this.originalContent = '';
    this.isTranslated = false;
  }

  // Public method to check if content is currently translated
  public getCurrentTranslationState(): boolean {
    return this.isTranslated;
  }

  // Handle translate button click
  handleTranslate() {
    const currentText = this.formControl.value || '';
    
    if (!currentText.trim()) {
      this.snackBar.open('No text to translate', 'Close', { duration: 2000 });
      return;
    }

    // Check if we should revert to original
    if (this.isTranslated && this.originalContent) {
      // Revert to original content
      this.formControl.setValue(this.originalContent);
      this.isTranslated = false;
      this.originalContent = '';
      this.snackBar.open('Reverted to original language', 'Close', { duration: 2000 });
      
      // Focus textarea and move cursor to end
      setTimeout(() => {
        this.textarea.nativeElement.focus();
        const length = this.formControl.value.length;
        this.textarea.nativeElement.selectionStart = length;
        this.textarea.nativeElement.selectionEnd = length;
      });
      return;
    }

    // Store original content before first translation
    if (!this.isTranslated) {
      this.originalContent = currentText;
    }

    // First detect the language
    this.translationService.detectLanguage(currentText).subscribe({
      next: (detectResult) => {
        // Open translation dialog
        const dialogRef = this.dialog.open(TranslationDialogComponent, {
          width: '400px',
          data: {
            currentText: currentText,
            detectedLanguage: detectResult.detectedLanguage
          }
        });

        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            // Show loading state
            this.isTranslating = true;
            const loadingSnackBar = this.snackBar.open('Translating message...', '', { duration: 0 });

            // Perform translation
            this.translationService.translateText(currentText, result.targetLanguage).subscribe({
              next: (translationResult) => {
                loadingSnackBar.dismiss();
                this.isTranslating = false;

                // Update the content with translated text
                this.formControl.setValue(translationResult.translatedText);
                this.isTranslated = true;

                // Focus textarea and move cursor to end
                setTimeout(() => {
                  this.textarea.nativeElement.focus();
                  const length = translationResult.translatedText.length;
                  this.textarea.nativeElement.selectionStart = length;
                  this.textarea.nativeElement.selectionEnd = length;
                });

                // Get language names safely
                const sourceLanguage = translationResult.sourceLanguage || detectResult.detectedLanguage || 'auto';
                const targetLanguage = translationResult.targetLanguage || result.targetLanguage || 'unknown';
                
                this.snackBar.open(
                  `Translated from ${this.getLanguageName(sourceLanguage)} to ${this.getLanguageName(targetLanguage)}`,
                  'Close',
                  { duration: 3000 }
                );
              },
              error: (error) => {
                loadingSnackBar.dismiss();
                this.isTranslating = false;
                this.snackBar.open('Translation failed: ' + error.message, 'Close', { duration: 4000 });
              }
            });
          }
        });
      },
      error: (error) => {
        this.snackBar.open('Language detection failed', 'Close', { duration: 3000 });
      }
    });
  }

  getLanguageName(code: string | undefined | null): string {
    if (!code) {
      return 'Unknown';
    }
    
    const languages: { [key: string]: string } = {
      'en': 'English',
      'es': 'Spanish',
      'fr': 'French',
      'de': 'German',
      'it': 'Italian',
      'pt': 'Portuguese',
      'zh': 'Chinese',
      'ja': 'Japanese',
      'ko': 'Korean',
      'ar': 'Arabic',
      'hi': 'Hindi',
      'ru': 'Russian'
    };
    return languages[code] || code.toUpperCase();
  }

  setupTemplateSearch() {
    // Setup debounced search
    this.templateSearchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe(searchValue => {
        this.templatePageIndex = 1;
        // Don't clear templates array to prevent re-render
        this.loadTemplates();
      });
  }

  onTemplateSearchChange(event: Event) {
    const searchValue = (event.target as HTMLInputElement).value;
    this.templateSearchSubject.next(searchValue);
  }

  onTemplateMenuScroll(event: Event) {
    const element = event.target as HTMLElement;
    const threshold = 0.8; // Load more when 80% scrolled
    
    if (element.scrollTop + element.clientHeight >= element.scrollHeight * threshold) {
      if (!this.isLoadingMoreTemplates && this.hasMoreTemplates) {
        this.loadMoreTemplates();
      }
    }
  }

  async loadMoreTemplates() {
    if (this.isLoadingMoreTemplates || !this.hasMoreTemplates) return;
    
    this.isLoadingMoreTemplates = true;
    this.templatePageIndex++;
    
    try {
      await this.loadTemplates(true);
    } finally {
      this.isLoadingMoreTemplates = false;
    }
  }

  async loadTemplates(append: boolean = false) {
    if (!append) {
      this.isLoadingTemplates = true;
    }

    let params: any = {
      type: 'sms',
      isSendingMessage: true,
      pageIndex: this.templatePageIndex,
      pageSize: this.templatePageSize,
      sortField: '',
      sortDirection: '',
      searchValue: this.templateSearchControl.value || '',
      showArchive: 'false'  // Keep as string since backend expects string
    };

    if (this.lead && this.lead?.program) {
      params.program_id = this.lead.program.id;
    }
    
    try {
      const response = await firstValueFrom(
        this.templateService.get(params),
      );
      
      // Temporary fix: limit to pageSize even if backend returns more
      const limitedData = response.data.slice(0, this.templatePageSize);
      
      if (append) {
        this.templates = [...this.templates, ...limitedData];
      } else {
        this.templates = limitedData;
      }
      
      this.templateTotal = response.total;
      this.hasMoreTemplates = this.templates.length < this.templateTotal;
      
      // Restore focus to search input after loading
      this.focusSearchInput();
    } finally {
      this.isLoadingTemplates = false;
    }
  }

  onTemplateMenuOpened() {
    // Reset pagination when menu opens
    if (this.isSendingMessage) {
      this.templatePageIndex = 1;
      this.templateSearchControl.setValue('');
      this.loadTemplates();
      
      // Focus search input when menu opens
      setTimeout(() => {
        this.focusSearchInput();
      }, 100);
    }
  }

  focusSearchInput() {
    if (this.templateSearchInput && this.templateSearchInput.nativeElement) {
      setTimeout(() => {
        this.templateSearchInput.nativeElement.focus();
      }, 0);
    }
  }

  toggleEmojiPicker(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.showEmojiPicker = !this.showEmojiPicker;
  }

  addEmoji(event: any): void {
    const emoji = event.emoji.native; // Get the emoji from the event
    this.insertAtCursor(emoji);
    this.showEmojiPicker = false; // Hide the picker after selection
    // Reset translation state when emoji is added
    this.resetTranslationState();
  }

  stopEvent(event: any) {
    if (!event.emoji) {
      event.stopPropagation();
    }
  }

  async onSettingsClick(event: Event, item: any, menuTrigger?: MatMenuTrigger) {
    event.stopPropagation();
    const dialogRef = this.dialog.open(SettingsDialogComponent, {
      width: '500px',
      data: {
        title: `Default value for ${item.label}`,
        defaultValue: item?.advanceSearchTypeSettings?.default
      }
    });

    // Handle the dialog close event
    dialogRef.afterClosed().subscribe(async result => {
      if (result !== undefined) {
        const response = await firstValueFrom(
          this.advanceSearch.createDefaultValue({
            defaultValue: result?.defaultValue,
            advance_search_type_id: item.id
          })
        );

        if (response) {
          // Update in otherFields array
          const index = this.otherFields.findIndex((field: any) => field.id === item.id);

          if (index !== -1) {
            this.otherFields[index] = {
              ...this.otherFields[index],
              advanceSearchTypeSettings: {
                ...this.otherFields[index].advanceSearchTypeSettings,
                default: result?.defaultValue
              }
            };

            // Force change detection
            this.otherFields = [...this.otherFields];
          }

          const leadIndex = this.leadTags.findIndex((field: any) => field.id === item.id);
          
          if (leadIndex !== -1) {
            this.leadTags[leadIndex] = {
              ...this.leadTags[leadIndex],
              advanceSearchTypeSettings: {
                ...this.leadTags[leadIndex].advanceSearchTypeSettings,
                default: result?.defaultValue
              }
            };
            this.leadTags = [...this.leadTags];
          }
        }

        menuTrigger?.closeMenu();
      }
    });
  }

  async onCreateTemplate(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    
    // Close the menu
    this.templateMenuTrigger?.closeMenu();
    
    // Dynamic import to avoid circular dependency
    const { AddEditTemplatesComponent } = await import('src/app/admissions/templates/add-edit-templates/add-edit-templates.component');
    
    const dialogRef = this.dialog.open(AddEditTemplatesComponent, {
      width: '90%',
      maxWidth: '1200px',
      height: '90%',
      maxHeight: '90vh',
      data: {
        isModal: true,
        defaultType: 'sms' // Set default type to SMS since we're in SMS component
      },
      panelClass: 'template-edit-dialog'
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      // expecting: { id: "123", action: "created"|"updated", type: "sms" }
      if (!result?.id) return;
    
      this.templatePageIndex = 1;
      await this.loadTemplates(); // make sure list includes the new one
    
      const created = this.templates.find((t: any) => String(t.id) === String(result.id));
      if (created) {
        this.onTemplateSelect(created); // applies message and emits attachments
      }
    
      setTimeout(() => this.templateMenuTrigger?.openMenu(), 300);
    });
  }

  async onEditTemplate(event: Event, template: any) {
    event.stopPropagation();
    event.preventDefault();
    
    // Dynamic import to avoid circular dependency
    const { AddEditTemplatesComponent } = await import('src/app/admissions/templates/add-edit-templates/add-edit-templates.component');
    
    const dialogRef = this.dialog.open(AddEditTemplatesComponent, {
      width: '90%',
      maxWidth: '1200px',
      height: '90%',
      maxHeight: '90vh',
      data: {
        templateId: template.id,
        isModal: true
      },
      panelClass: 'template-edit-dialog'
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (!result?.id) return;
    
      this.templatePageIndex = 1;
      await this.loadTemplates();
    
      const updated = this.templates.find((t: any) => String(t.id) === String(result.id));
      if (updated) this.onTemplateSelect(updated);
    });
  }

  async onTagSelect(value: any) {
    const findExist = this.recent.find((x: any) => x.id === value.id);
    if (!findExist) {
      const response = await firstValueFrom(this.advanceSearch.lastUsedUpdate({}, value.id));
      if (response) {
        if (this.recent.length == 5) {
          this.recent.pop();
          this.recent.unshift(value);
        } else {
          this.recent.unshift(value);
        }
      }
    }

    if (this.isSendingMessage && this.allowConvert) {
      const rawValue = value.entitySearch
        ? this.lead[value.relationField]?.[value.entitySearch] ?? value?.advanceSearchTypeSettings?.default ?? 'null'
        : this.lead[value.field] ?? value?.advanceSearchTypeSettings?.default ?? 'null';
    
      const formattedValue = this.formatDateIfNeeded(rawValue);
      const convertValue = `${formattedValue}`;
      this.insertAtCursor(convertValue);
    } else {
      const entitySuffix = value.entitySearch ? `.${value.entitySearch}` : '';
      const convertValue = `[[${value.entitySearch ? value.relationField : value.field}${entitySuffix}]]`;
      this.insertAtCursor(convertValue);
    }
    
    // Reset translation state when tag is inserted
    this.resetTranslationState();
  }

  insertAtCursor(text: string) {
    const textarea = this.textarea.nativeElement as HTMLTextAreaElement;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
  
    const currentValue = this.formControl.value || '';
    const newValue =
      currentValue.substring(0, start) + text + ' ' + currentValue.substring(end);
  
    this.formControl.setValue(newValue);
  
    // Move the cursor to the end of the inserted text plus space
    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd = start + text.length + 1;
      textarea.focus();
    });
  }

  async loadTags() {
    const response = await firstValueFrom(
      this.templateService.get({}, 'fields'),
    );

    this.leadTags = [];
    this.otherFields = [];
    this.recent = response.recent;
    this.allFields = response.all

    response.all.forEach((item: any) => {
      if (item.entity === 'leads' && !item.entitySearch) {
        this.leadTags.push(item);
      } else {
        this.otherFields.push(item);
      }
    });
  }

  onTemplateSelect(event: any) {
    // Reset translation state when selecting a new template
    this.resetTranslationState();
    
    if (event.attachments?.length > 0) {
      const eventCopy = JSON.parse(JSON.stringify(event.attachments));
      this.templateChosen.emit(eventCopy);
    } else {
      this.templateChosen.emit([]);
    }
    
    if (this.allowConvert && this.isSendingMessage) {
      this.currentTemplate = this.replacePlaceholders(event.message, this.lead);
    } else {
      this.currentTemplate = event.message;
    }
  
    this.formControl.setValue(this.currentTemplate);
  
    // Focus the textarea and move cursor to the end of the text
    setTimeout(() => {
      this.textarea.nativeElement.focus();
      const length = this.currentTemplate.length;
      this.textarea.nativeElement.selectionStart = length;
      this.textarea.nativeElement.selectionEnd = length;
    });
  }

  onChange = (value: any) => {};
  onTouched = () => {};

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(value: any): void {
    this.value = value;
    // Reset translation state when new value is written
    if (value !== this.value) {
      this.resetTranslationState();
    }
  }

  replacePlaceholders(template: string, data: any): string {
    const allFields = this.allFields || [];
  
    return template.replace(/\[\[([a-zA-Z0-9_.]+)\]\]/g, (_, key) => {
      const segments = key.split('.');
      let value: any = data;
      let missing = false;
  
      // Try to get the value from the data object
      for (const seg of segments) {
        if (value != null && typeof value === 'object' && seg in value) {
          value = value[seg];
        } else {
          missing = true;
          break;
        }
      }
  
      // If we found a value, format and return it
      if (!missing && value != null) {
        const formattedValue = this.formatDateIfNeeded(String(value));
        return formattedValue;
      }
  
      // If no value found, look for default value in field configuration
      let field = null;
      
      if (segments.length === 1) {
        // Simple field case (e.g., "firstname")
        field = allFields.find(f => f.field === key && !f.entitySearch);
      } else if (segments.length === 2) {
        // Nested field case (e.g., "campus.name")
        const [relationField, entitySearch] = segments;
        field = allFields.find(f => 
          f.relationField === relationField && 
          f.entitySearch === entitySearch
        );
      }
  
      // Return default value if found, otherwise return 'null'
      if (field?.advanceSearchTypeSettings?.default != null) {
        const defaultValue = String(field.advanceSearchTypeSettings.default);
        const formattedDefault = this.formatDateIfNeeded(defaultValue);
        return formattedDefault;
      }
  
      return 'null';
    });
  }

  private formatDateIfNeeded(value: string): string {
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/;
    if (isoDateRegex.test(value)) {
      const date = new Date(value);
      const formatted = `${date.getFullYear()}-${(date.getMonth()+1).toString().padStart(2,'0')}-${date.getDate().toString().padStart(2,'0')} ${date.getHours().toString().padStart(2,'0')}:${date.getMinutes().toString().padStart(2,'0')}`;
      return formatted;
    }
    return value;
  }
}